/**
 * AgentEditor — the no-code agent builder canvas (Phase 3).
 *
 * Composes the {@link NodePalette}, a ReactFlow canvas bound to the
 * {@link useCanvasStore}, and the {@link ExecutionOverlay}. It:
 *   - loads an existing agent definition (`/agents/:id`) into the canvas, or
 *     starts a blank canvas for `/agents/new`;
 *   - lets the user add nodes (click or drag from the palette), wire edges, and
 *     rename / delete the selected node;
 *   - saves the graph back via `POST /agents` (create) or `PUT /agents/:id`
 *     (update), serialized through `canvasStore.toDefinition()`; and
 *   - runs the agent via `POST /agents/:id/run`, streaming SSE events into the
 *     ExecutionOverlay so each node lights up live.
 *
 * Auth is implicit: the api client injects the JWT, and the run stream forwards
 * the same bearer token (EventSource can't set headers, so a fetch stream is
 * used). Identity / persona comes from {@link useAuth}.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  type Connection,
  type ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { api, ApiError } from '../lib/api';
import { config } from '../lib/config';
import { fetchConnectors } from '../lib/connectors';
import { useAuth } from '../contexts/AuthContext';
import {
  NODE_TYPE_META,
  type AgentDefinition,
  type NodeType,
  type SaveAgentRequest,
} from '../lib/agents';
import { useCanvasStore } from '../stores/canvasStore';
import { canvasNodeTypes } from '../components/canvas/nodeTypes';
import { NodePalette } from '../components/canvas/NodePalette';
import { ExecutionOverlay } from '../components/canvas/ExecutionOverlay';

function AgentEditorInner(): ReactNode {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isNew = !agentId || agentId === 'new';

  const nodes = useCanvasStore((s) => s.nodes);
  const edges = useCanvasStore((s) => s.edges);
  const selectedNodeId = useCanvasStore((s) => s.selectedNodeId);
  const onNodesChange = useCanvasStore((s) => s.onNodesChange);
  const onEdgesChange = useCanvasStore((s) => s.onEdgesChange);
  const onConnect = useCanvasStore((s) => s.onConnect);
  const addNode = useCanvasStore((s) => s.addNode);
  const removeNode = useCanvasStore((s) => s.removeNode);
  const renameNode = useCanvasStore((s) => s.renameNode);
  const selectNode = useCanvasStore((s) => s.selectNode);
  const loadDefinition = useCanvasStore((s) => s.loadDefinition);
  const toDefinition = useCanvasStore((s) => s.toDefinition);
  const reset = useCanvasStore((s) => s.reset);

  const [name, setName] = useState('Untitled Agent');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);
  // Data source to point the agent at when running (a connector grain; '' = the
  // agent's configured source). And whether the developer-API panel is open.
  const [grain, setGrain] = useState('');
  const [showApi, setShowApi] = useState(false);
  const connectorsQuery = useQuery({ queryKey: ['connectors'], queryFn: fetchConnectors });

  // Load the existing definition (or reset for a fresh canvas).
  useEffect(() => {
    let cancelled = false;
    if (isNew) {
      reset();
      setName('Untitled Agent');
      setDescription('');
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get<AgentDefinition>(`/agents/${agentId}`)
      .then((def) => {
        if (cancelled) return;
        loadDefinition(def);
        setName(def.name);
        setDescription(def.description ?? '');
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load agent');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, isNew, loadDefinition, reset]);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId],
  );

  const handleAdd = useCallback(
    (type: NodeType) => {
      // Drop new nodes in a readable column; ReactFlow lets the user reposition.
      const position = { x: 120 + nodes.length * 40, y: 80 + nodes.length * 30 };
      addNode(type, position);
    },
    [addNode, nodes.length],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/agent-node-type');
      if (!type) return;
      const position = rfInstance
        ? rfInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY })
        : { x: 160, y: 120 };
      addNode(type as NodeType, position);
    },
    [addNode, rfInstance],
  );

  const handleConnect = useCallback(
    (connection: Connection) => onConnect(connection),
    [onConnect],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    const { nodes: wireNodes, edges: wireEdges } = toDefinition();
    const body: SaveAgentRequest = {
      name: name.trim() || 'Untitled Agent',
      description: description.trim() || null,
      nodes: wireNodes,
      edges: wireEdges,
    };
    try {
      const saved = isNew
        ? await api.post<AgentDefinition>('/agents', body)
        : await api.put<AgentDefinition>(`/agents/${agentId}`, body);
      // Navigate to the canonical agent URL after a create (stay in the builder).
      if (isNew) navigate(`/agents/${saved.id}`, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Failed to save agent');
    } finally {
      setSaving(false);
    }
  }, [agentId, description, isNew, name, navigate, toDefinition]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-steel-500">
        Loading agent…
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-3 pb-3">
        <input
          aria-label="Agent name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rounded-lg border border-steel-300 px-3 py-1.5 text-sm font-medium text-navy-900 w-64"
        />
        <input
          aria-label="Agent description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description"
          className="flex-1 rounded-lg border border-steel-300 px-3 py-1.5 text-sm text-steel-700"
        />
        <select
          aria-label="Data source"
          value={grain}
          onChange={(e) => setGrain(e.target.value)}
          title="Point the agent at a data source (connector) for this run"
          className="rounded-lg border border-steel-300 px-2 py-1.5 text-sm text-navy-900"
        >
          <option value="">Agent default source</option>
          {(connectorsQuery.data ?? []).map((c) => (
            <option key={c.id} value={c.grain}>
              {c.name} ({c.grain})
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="btn-accent"
          onClick={() => setRunning(true)}
          disabled={isNew || running}
          title={isNew ? 'Save the agent before running' : 'Run this agent'}
        >
          Run
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setShowApi((v) => !v)}
          disabled={isNew}
          title={isNew ? 'Save the agent to get its API call' : 'Show the developer API call'}
        >
          API
        </button>
      </div>

      {error && (
        <div className="mb-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="flex-1 flex border border-steel-200 rounded-lg overflow-hidden relative bg-white">
        <NodePalette onAdd={handleAdd} />

        <div
          className="flex-1 relative"
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
          }}
          onDrop={handleDrop}
          data-testid="agent-canvas"
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={canvasNodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={handleConnect}
            onInit={setRfInstance}
            onNodeClick={(_, node) => selectNode(node.id)}
            onPaneClick={() => selectNode(null)}
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background />
            <Controls />
            <MiniMap pannable zoomable />
          </ReactFlow>

          {running && agentId && !isNew && (
            <ExecutionOverlay
              agentId={agentId}
              inputPayload={{
                persona: user?.personaKey ?? null,
                ...(grain ? { grain } : {}),
              }}
              onClose={() => setRunning(false)}
            />
          )}

          {showApi && agentId && !isNew && (
            <ApiPanel agentId={agentId} grain={grain} onClose={() => setShowApi(false)} />
          )}
        </div>

        {/* Inspector for the selected node */}
        {selectedNode && (
          <aside className="w-64 shrink-0 border-l border-steel-200 bg-steel-50 p-4 overflow-y-auto">
            <h3 className="text-sm font-semibold text-navy-900">Node</h3>
            <p className="mt-1 text-[11px] font-mono text-steel-500">
              {NODE_TYPE_META[selectedNode.data.type].label} · {selectedNode.data.type}
            </p>
            <label className="block mt-3 text-xs font-medium text-steel-600">
              Name
              <input
                aria-label="Node name"
                value={selectedNode.data.name}
                onChange={(e) => renameNode(selectedNode.id, e.target.value)}
                className="mt-1 w-full rounded-md border border-steel-300 px-2 py-1 text-sm"
              />
            </label>
            <button
              type="button"
              className="btn-secondary w-full mt-4"
              onClick={() => removeNode(selectedNode.id)}
            >
              Delete node
            </button>
          </aside>
        )}
      </div>
    </div>
  );
}

/**
 * ApiPanel — the developer API for calling this agent from an automated
 * workflow. Shows a ready-to-run curl for the synchronous `/invoke` endpoint
 * (returns the final output as JSON) plus a note on the streaming `/run`
 * variant. The bearer token comes from `POST /api/auth/login`.
 */
function ApiPanel({
  agentId,
  grain,
  onClose,
}: {
  agentId: string;
  grain: string;
  onClose: () => void;
}): ReactNode {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const base = `${origin}${config.apiBaseUrl}`;
  const body = JSON.stringify({
    input_payload: { query: 'your question', ...(grain ? { grain } : {}) },
  });
  const snippet = [
    `# Get a token, then call the agent and read JSON output:`,
    `curl -X POST ${base}/agents/${agentId}/invoke \\`,
    `  -H "Authorization: Bearer $TRIDENT_TOKEN" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '${body}'`,
  ].join('\n');

  return (
    <div
      className="absolute top-0 right-0 h-full w-96 bg-white border-l border-steel-200 shadow-xl flex flex-col z-10"
      data-testid="api-panel"
    >
      <div className="px-4 py-3 border-b border-steel-200 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-navy-900">Developer API</h3>
          <p className="text-[11px] text-steel-500">Call this agent from a workflow</p>
        </div>
        <button type="button" className="text-steel-500 hover:text-navy-700" onClick={onClose}>
          ✕
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 text-sm text-steel-700">
        <p>
          Synchronous run — returns the final output as JSON (no streaming). Same
          auth, permission filter, and audit as the UI run.
        </p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-navy-900 p-3 text-[11px] leading-relaxed text-steel-100 whitespace-pre-wrap">
          {snippet}
        </pre>
        <button
          type="button"
          className="btn-secondary mt-2"
          onClick={() => {
            void navigator.clipboard?.writeText(snippet);
          }}
        >
          Copy
        </button>
        <p className="mt-4 text-xs text-steel-500">
          Streaming variant: <span className="font-mono">POST /agents/{agentId}/run</span>{' '}
          (Server-Sent Events). Full spec at <span className="font-mono">/docs</span>.
        </p>
      </div>
    </div>
  );
}

export function AgentEditor(): ReactNode {
  // ReactFlowProvider supplies the instance context used by screenToFlowPosition.
  return (
    <ReactFlowProvider>
      <AgentEditorInner />
    </ReactFlowProvider>
  );
}

export default AgentEditor;
